import React, { useState, useLayoutEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDrawerHeaderFlushSx,
  harmonateDrawerBodySx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateTabsSx,
  harmonateFormFieldsSx,
  harmonateSwitchSx,
  harmonateDrawerEditIconSx,
  harmonateDrawerDeleteIconSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const dataTypeOptions = ['TEXT', 'NUMBER', 'BOOLEAN', 'DATE', 'DATETIME', 'CURRENCY'];

/** Select menus portal with default z-index; must sit above nested Dialog (this drawer uses modal + 150). */
const selectMenuAboveNestedDialogProps = {
  MenuProps: {
    PaperProps: {
      sx: (theme) => ({ zIndex: theme.zIndex.modal + 200 }),
    },
    sx: (theme) => ({ zIndex: theme.zIndex.modal + 200 }),
  },
};

const cloneMapping = (mapping) => {
  if (!mapping || typeof mapping !== 'object') return {};
  try {
    return JSON.parse(JSON.stringify(mapping));
  } catch {
    return { ...mapping };
  }
};

const parseApiMapping = (raw) => {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    try {
      const o = JSON.parse(raw);
      return typeof o === 'object' && o ? o : {};
    } catch {
      return {};
    }
  }
  return typeof raw === 'object' ? { ...raw } : {};
};

/**
 * Same mapping-field editor as Reference Templates (drawer + add/edit/delete field),
 * for use from GL Sync upload without leaving this screen.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {object|null} apiTemplate — row from `ref-templates` APIs (`templateId`, `templateName`, `mapping`, …)
 * @param {() => string|null} getAuthToken
 * @param {() => void} [onSaved] — called after successful PUT so parent can refetch templates (ignored when `persistToApi` is false)
 * @param {boolean} [persistToApi=true] — when false, add/edit/delete only updates local state and calls `onLocalMappingChange` (same idea as upload-only surrogate key edits)
 * @param {(templateId: string, mapping: object) => void} [onLocalMappingChange] — when `persistToApi` is false, called with the new raw `mapping` after each change
 */
const RefTemplateMappingsEditorDrawer = ({
  open,
  onClose,
  apiTemplate,
  getAuthToken,
  onSaved,
  persistToApi = true,
  onLocalMappingChange,
}) => {
  const { canAdd, canEdit } = useFunctionPermissions(FC.GL_SYNC);
  const [workingTemplate, setWorkingTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('grid');
  const [editMappingDialogOpen, setEditMappingDialogOpen] = useState(false);
  const [currentMapping, setCurrentMapping] = useState({
    sourceField: '',
    dataType: 'TEXT',
    isActive: true,
    targetColumn: '',
  });
  const [editingField, setEditingField] = useState(null);
  const [deleteFieldDialogOpen, setDeleteFieldDialogOpen] = useState(false);
  const [deleteField, setDeleteField] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toastState, setToastState] = useState({ open: false, message: '', severity: 'info' });

  const showToast = useCallback((message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  }, []);
  const hideToast = useCallback(() => {
    setToastState((prev) => ({ ...prev, open: false }));
  }, []);

  useLayoutEffect(() => {
    if (!open || !apiTemplate) {
      setWorkingTemplate(null);
      return;
    }
    const templateId = apiTemplate.templateId ?? apiTemplate.id;
    setWorkingTemplate({
      ...apiTemplate,
      templateId,
      templateName: apiTemplate.templateName ?? apiTemplate.name ?? 'Template',
      mapping: parseApiMapping(apiTemplate.mapping),
    });
    setActiveTab('grid');
  }, [open, apiTemplate]);

  const handleOpenEditMapping = useCallback((field = null, mappingData = null) => {
    if (field && mappingData) {
      setEditingField(field);
      setCurrentMapping({
        sourceField: field,
        dataType: mappingData.dataType || 'TEXT',
        isActive: mappingData.isActive !== false,
        targetColumn: mappingData.targetColumn || '',
      });
    } else {
      setEditingField(null);
      setCurrentMapping({
        sourceField: '',
        dataType: 'TEXT',
        isActive: true,
        targetColumn: '',
      });
    }
    setEditMappingDialogOpen(true);
  }, []);

  const mappingRows = useMemo(
    () =>
      Object.entries(workingTemplate?.mapping || {}).map(([sourceField, config]) => ({
        id: sourceField,
        sourceField,
        targetColumn: config?.targetColumn || sourceField,
        dataType: config?.dataType || 'TEXT',
        isActive: config?.isActive !== false,
      })),
    [workingTemplate]
  );

  const mappingColumns = useMemo(
    () => [
      { field: 'sourceField', headerName: 'Source Field', flex: 1, minWidth: 150 },
      { field: 'targetColumn', headerName: 'Target Column', flex: 1, minWidth: 150 },
      { field: 'dataType', headerName: 'Data Type', flex: 1, minWidth: 120 },
      {
        field: 'isActive',
        headerName: 'Active',
        width: 80,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Switch checked={params.value} disabled size="small" />
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEditMapping(params.row.sourceField, {
                    dataType: params.row.dataType,
                    targetColumn: params.row.targetColumn,
                    isActive: params.row.isActive,
                  });
                }}
                disabled={submitting}
                sx={harmonateDrawerEditIconSx}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteField(params.row.sourceField);
                  setDeleteFieldDialogOpen(true);
                }}
                sx={harmonateDrawerDeleteIconSx}
                disabled={submitting}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [submitting, handleOpenEditMapping]
  );

  const putTemplateMapping = async (nextMapping) => {
    const token = getAuthToken?.();
    if (!token || !workingTemplate?.templateId) {
      throw new Error('Missing authentication or template id');
    }
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');
    const url = `${base}conductor/ref-templates/${encodeURIComponent(workingTemplate.templateId)}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...workingTemplate,
        mapping: nextMapping,
      }),
    });
    if (!response.ok) {
      const t = await response.text().catch(() => '');
      throw new Error(t || `Update failed (${response.status})`);
    }
    const updated = await response.json().catch(() => null);
    if (updated && typeof updated === 'object') {
      const tid = updated.templateId ?? updated.id ?? workingTemplate.templateId;
      setWorkingTemplate({
        ...updated,
        templateId: tid,
        templateName: updated.templateName ?? updated.name ?? workingTemplate.templateName,
        mapping: parseApiMapping(updated.mapping),
      });
    } else {
      setWorkingTemplate((prev) => (prev ? { ...prev, mapping: cloneMapping(nextMapping) } : prev));
    }
    return true;
  };

  const handleSaveMapping = async () => {
    if (!workingTemplate || !currentMapping.sourceField?.trim()) return;
    try {
      setSubmitting(true);
      const updatedMapping = cloneMapping(workingTemplate.mapping);
      if (editingField && editingField !== currentMapping.sourceField) {
        delete updatedMapping[editingField];
      }
      updatedMapping[currentMapping.sourceField.trim()] = {
        dataType: currentMapping.dataType,
        isActive: currentMapping.isActive,
        targetColumn: currentMapping.targetColumn?.trim() || currentMapping.sourceField.trim(),
      };
      if (!persistToApi) {
        const next = cloneMapping(updatedMapping);
        setWorkingTemplate((prev) => (prev ? { ...prev, mapping: next } : prev));
        const tid = workingTemplate.templateId ?? workingTemplate.id;
        onLocalMappingChange?.(String(tid), next);
        setEditMappingDialogOpen(false);
        showToast(
          'Mapping updated for this upload only. It is not saved to the reference template on the server.',
          'success'
        );
      } else {
        await putTemplateMapping(updatedMapping);
        setEditMappingDialogOpen(false);
        showToast('Mapping saved successfully', 'success');
        onSaved?.();
      }
    } catch (error) {
      console.error('RefTemplateMappingsEditorDrawer save:', error);
      showToast(error.message || 'Failed to save mapping', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDeleteField = async () => {
    if (!deleteField || !workingTemplate) return;
    try {
      setSubmitting(true);
      const updatedMapping = cloneMapping(workingTemplate.mapping);
      delete updatedMapping[deleteField];
      if (!persistToApi) {
        const next = cloneMapping(updatedMapping);
        setWorkingTemplate((prev) => (prev ? { ...prev, mapping: next } : prev));
        const tid = workingTemplate.templateId ?? workingTemplate.id;
        onLocalMappingChange?.(String(tid), next);
        setDeleteFieldDialogOpen(false);
        setDeleteField(null);
        showToast(
          'Field removed for this upload only. It is not saved to the reference template on the server.',
          'success'
        );
      } else {
        await putTemplateMapping(updatedMapping);
        setDeleteFieldDialogOpen(false);
        setDeleteField(null);
        showToast('Mapping deleted successfully', 'success');
        onSaved?.();
      }
    } catch (error) {
      console.error('RefTemplateMappingsEditorDrawer delete field:', error);
      showToast(error.message || 'Failed to delete mapping', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const hasMappings = workingTemplate?.mapping && Object.keys(workingTemplate.mapping).length > 0;

  return (
    <>
      <Drawer
        anchor="right"
        open={open && Boolean(workingTemplate)}
        onClose={onClose}
        sx={{
          /* Default Drawer z-index (1200) is below Dialog (1300), so the editor hid behind upload modals. */
          zIndex: (theme) => theme.zIndex.modal + 50,
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '44%' },
            minWidth: 380,
            maxWidth: 720,
            bgcolor: ht.drawerBg,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {workingTemplate && (
          <>
            <Box sx={harmonateDrawerHeaderFlushSx}>
              <Typography variant="h6" sx={{ color: ht.white, fontWeight: 700 }}>
                {workingTemplate.templateName || 'Template'}
              </Typography>
              <IconButton onClick={onClose} aria-label="Close" sx={harmonateDialogCloseIconSx}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={harmonateDrawerBodySx}>
              {hasMappings ? (
                <>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenEditMapping()}
                      disabled={submitting || !canAdd}
                      sx={harmonateProceedButtonSx}
                    >
                      Add Field
                    </Button>
                  </Box>

                  <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{ ...harmonateTabsSx, mb: 2, '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' } }}
                  >
                    <Tab label="Grid View" value="grid" />
                    <Tab label="Raw JSON" value="json" />
                  </Tabs>

                  <Box
                    sx={{
                      p: 2,
                      bgcolor: ht.white,
                      borderRadius: ht.cardRadius,
                      maxHeight: 'calc(100vh - 220px)',
                      overflow: 'auto',
                    }}
                  >
                    {activeTab === 'grid' ? (
                      <DataGridPro
                        columnResizeMode="onEnd"
                        pagination
                        rowsPerPageOptions={[10, 25, 50]}
                        pageSize={10}
                        autoPageSize={false}
                        resizable
                        rows={mappingRows}
                        columns={mappingColumns}
                        getRowId={(row) => row.id}
                        autoHeight
                        disableRowSelectionOnClick
                        hideFooter
                        sx={harmonateDataGridSx}
                        components={{
                          NoRowsOverlay: () => (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: 200,
                              }}
                            >
                              <Typography>No mappings defined</Typography>
                            </Box>
                          ),
                        }}
                      />
                    ) : (
                      <Box sx={{ p: 2, bgcolor: ht.listBg, borderRadius: ht.fieldRadius }}>
                        <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(workingTemplate.mapping, null, 2)}
                        </pre>
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 280,
                    textAlign: 'center',
                    color: ht.text,
                    border: `1px dashed ${ht.line}`,
                    borderRadius: ht.cardRadius,
                    p: 4,
                    bgcolor: ht.white,
                  }}
                >
                  <AddIcon fontSize="large" sx={{ mb: 2, color: ht.fadedText }} />
                  <Typography variant="h6" gutterBottom sx={{ color: ht.text }}>
                    No mapping data
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, maxWidth: 360, color: ht.text }}>
                    Add field mappings here; changes are saved to the same template used on the GL Sync upload screen.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenEditMapping()}
                    disabled={submitting || !canAdd}
                    sx={harmonateProceedButtonSx}
                  >
                    Add first field
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </Drawer>

      <Dialog
        open={deleteFieldDialogOpen}
        onClose={() => !submitting && setDeleteFieldDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 150 }}
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>Delete the mapping for &quot;{deleteField}&quot;?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFieldDialogOpen(false)} disabled={submitting} sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDeleteField} variant="contained" disabled={submitting} sx={harmonateAbortButtonSx}>
            {submitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editMappingDialogOpen}
        onClose={() => !submitting && setEditMappingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 150 }}
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>{editingField ? 'Edit mapping' : 'Add mapping'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Source field"
              value={currentMapping.sourceField}
              onChange={(e) => setCurrentMapping({ ...currentMapping, sourceField: e.target.value })}
              fullWidth
              required
              disabled={submitting || Boolean(editingField)}
            />
            <FormControl fullWidth>
              <InputLabel>Data type</InputLabel>
              <Select
                value={currentMapping.dataType}
                label="Data type"
                onChange={(e) => setCurrentMapping({ ...currentMapping, dataType: e.target.value })}
                disabled={submitting}
                {...selectMenuAboveNestedDialogProps}
              >
                {dataTypeOptions.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Target column"
              value={currentMapping.targetColumn}
              onChange={(e) => setCurrentMapping({ ...currentMapping, targetColumn: e.target.value })}
              fullWidth
              disabled={submitting}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={currentMapping.isActive}
                  onChange={(e) => setCurrentMapping({ ...currentMapping, isActive: e.target.checked })}
                  sx={harmonateSwitchSx}
                  disabled={submitting}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMappingDialogOpen(false)} disabled={submitting} sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveMapping}
            variant="contained"
            disabled={!currentMapping.sourceField?.trim() || submitting}
            sx={harmonateProceedButtonSx}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast open={toastState.open} onClose={hideToast} message={toastState.message} severity={toastState.severity} />
    </>
  );
};

export default RefTemplateMappingsEditorDrawer;
